import forge from 'node-forge'

/**
 * Decrypts an encrypted card field using AES-GCM
 *
 * The RAIN provider encrypts sensitive card data (card number, CVV) using:
 * - AES-256-GCM encryption
 * - 16-byte authentication tag appended to ciphertext
 * - Base64 encoding for transport
 *
 * @param base64Secret - Base64 encoded ciphertext + auth tag
 * @param base64Iv - Base64 encoded initialization vector
 * @param secretKey - Hex-encoded secret key (from generateSessionId)
 * @returns Decrypted field value as UTF-8 string
 */
export async function decryptCardField(
  base64Secret: string,
  base64Iv: string,
  secretKey: string,
): Promise<string> {
  if (!base64Secret) {
    throw new Error('base64Secret is required for decryption')
  }

  if (!base64Iv) {
    throw new Error('base64Iv is required for decryption')
  }

  if (!secretKey || !/^[0-9A-Fa-f]+$/.test(secretKey)) {
    throw new Error('secretKey must be a valid hex string')
  }

  // Decode base64 inputs to buffers
  const secretBuffer = Buffer.from(base64Secret, 'base64')
  const ivBuffer = Buffer.from(base64Iv, 'base64')
  const keyBuffer = Buffer.from(secretKey, 'hex')

  // GCM authentication tag length (16 bytes)
  const tagLength = 16

  // Split ciphertext and auth tag
  // The auth tag is appended to the end of the ciphertext
  const ciphertextBuffer = secretBuffer.slice(0, secretBuffer.length - tagLength)
  const authTagBuffer = secretBuffer.slice(secretBuffer.length - tagLength)

  // Convert to binary strings for node-forge
  const ciphertextBytes = ciphertextBuffer.toString('binary')
  const ivBytes = ivBuffer.toString('binary')
  const keyBytes = keyBuffer.toString('binary')
  const tagBytes = authTagBuffer.toString('binary')

  // Create AES-GCM decipher
  const decipher = forge.cipher.createDecipher('AES-GCM', keyBytes)

  // Initialize with IV and auth tag
  decipher.start({
    iv: ivBytes,
    tagLength: tagLength * 8, // Tag length in bits
    tag: forge.util.createBuffer(tagBytes),
  })

  // Process ciphertext
  decipher.update(forge.util.createBuffer(ciphertextBytes))

  // Finish decryption and verify auth tag
  const pass = decipher.finish()

  if (!pass) {
    throw new Error(
      'Failed to decrypt card field: Authentication tag verification failed. ' +
        'This may indicate data tampering or incorrect key.',
    )
  }

  // Get decrypted output and decode from UTF-8
  const binaryString = decipher.output.toString()

  return forge.util.decodeUtf8(binaryString).trim()
}
